// ***************************************
// tgfAudio
//
// This object deals with the audio.
//
// Copyright 2015 Tingly Games B.V.
// Last Changed: 26-6-2015
// ***************************************
import { tgfFunctions } from "./tgfFunctions";
import { tbAudio } from "./../engine/tbAudio";
import { tgfSettings } from "./tgfSettings";
import { tgfStatus } from "./tgfStatus";
import { a_ghostbar_fill } from "../code/AssetDefinitions/SoundDefinitions";
tgfSettings.audioDefaults = {
    silentSound: undefined,
    effectsTag: ["sfx", "effect", "game"],
    musicTag: "music",
    effectsAvailable: true,
    musicAvailable: true,
    effectsAllowed: true,
    musicAllowed: true,
    musicMuted: false,
    effectsMuted: false,
    musicGain: 1,
    effectsGain: 1 // Global gain for the sound effects
};
var TGFAudio = /** @class */ (function () {
    function TGFAudio() {
    }
    // ***************************************
    // Initialization
    // ***************************************
    // Initialize the audio
    TGFAudio.prototype.initialize = function (settings) {
        var preferences = tgfStatus.getPreferencesData();
        tgfSettings.audioDefaults.silentSound = a_ghostbar_fill;
        this._setProperties(tgfSettings.audioDefaults);
        this._setProperties(settings);
        // Play the first silent sound
        this._handleFirstSound();
        // Read the settings from the player
        if (preferences.effectsGain !== undefined) {
            this.effectsGain = preferences.effectsGain;
        }
        if (preferences.musicGain !== undefined) {
            this.musicGain = preferences.musicGain;
        }
        if (preferences.effectsMuted !== undefined) {
            this.effectsMuted = preferences.effectsMuted;
        }
        if (preferences.musicMuted !== undefined) {
            this.musicMuted = preferences.musicMuted;
        }
        // Set the global gain and mute
        this.setEffectsGain(this.effectsGain);
        this.setMusicGain(this.musicGain);
        if (this.effectsMuted) {
            this.muteEffects();
        }
        else {
            this.unmuteEffects();
        }
        if (this.musicMuted) {
            this.muteMusic();
        }
        else {
            this.unmuteMusic();
        }
    };
    // Set a number of properties
    TGFAudio.prototype._setProperties = function (properties) {
        if (typeof properties !== "object") {
            return;
        }
        for (var property in properties) {
            if (properties.hasOwnProperty(property)) {
                this[property] = tgfFunctions.copyValue(properties[property]);
            }
        }
    };
    // ***************************************
    // Deal with playing first sound in user event
    //		- This is required by e.g. iOS
    // ***************************************
    // Returns whether the sound is an effect
    TGFAudio.prototype._handleFirstSound = function () {
        var _this = this;
        if (this.silentSound === undefined) {
            return;
        }
        this._silentSoundPlayed = false;
        // Define the callback function
        var playSilentSound = function () {
            // Check whether already done
            if (_this._silentSoundPlayed) {
                return;
            }
            _this._silentSoundPlayed = true;
            // Play the sound
            tbAudio.play(_this.silentSound);
            // Remove the event listeners
            window.removeEventListener("click", playSilentSound, false);
            window.removeEventListener("mousedown", playSilentSound, false);
            window.removeEventListener("touchstart", playSilentSound, false);
        };
        // Add it in a few event listeners
        window.addEventListener("click", playSilentSound, false);
        window.addEventListener("mousedown", playSilentSound, false);
        window.addEventListener("touchstart", playSilentSound, false);
    };
    // ***************************************
    // Checking what a sound is
    // ***************************************
    // Returns whether the sound is an effect
    TGFAudio.prototype.isEffect = function (sound) {
        var soundTags = sound.getTags(), tags = [].concat(this.effectsTag), i;
        for (i = 0; i < tags.length; i += 1) {
            if (soundTags.indexOf(tags[i]) >= 0) {
                return true;
            }
        }
        return false;
    };
    // Returns whether the sound is music
    TGFAudio.prototype.isMusic = function (sound) {
        var soundTags = sound.getTags(), tags = [].concat(this.musicTag), i;
        for (i = 0; i < tags.length; i += 1) {
            if (soundTags.indexOf(tags[i]) >= 0) {
                return true;
            }
        }
        return false;
    };
    // Returns whether a sound can be played
    TGFAudio.prototype.canPlay = function (sound) {
        if (this.isEffect(sound) && this.effectsAvailable && this.effectsAllowed) {
            return true;
        }
        if (this.isMusic(sound) && this.musicAvailable && this.musicAllowed) {
            return true;
        }
        return false;
    };
    // ***************************************
    // Muting and setting the global gain
    // ***************************************
    // Mute all effects
    TGFAudio.prototype.muteEffects = function () {
        var i, tags = [].concat(this.effectsTag);
        if (this.effectsAvailable && this.effectsAllowed) {
            for (i = 0; i < tags.length; i += 1) {
                tbAudio.mute(tags[i]);
            }
        }
    };
    // Mute all music
    TGFAudio.prototype.muteMusic = function () {
        var i, tags = [].concat(this.musicTag);
        if (this.musicAvailable && this.musicAllowed) {
            for (i = 0; i < tags.length; i += 1) {
                tbAudio.mute(tags[i]);
            }
        }
    };
    // Mute all effects
    TGFAudio.prototype.unmuteEffects = function () {
        var i, tags = [].concat(this.effectsTag);
        if (this.effectsAvailable && this.effectsAllowed) {
            for (i = 0; i < tags.length; i += 1) {
                tbAudio.unmute(tags[i]);
            }
        }
    };
    // Mute all music
    TGFAudio.prototype.unmuteMusic = function () {
        var i, tags = [].concat(this.musicTag);
        if (this.musicAvailable && this.musicAllowed) {
            for (i = 0; i < tags.length; i += 1) {
                tbAudio.unmute(tags[i]);
            }
        }
    };
    // Set gain for all effects
    TGFAudio.prototype.setEffectsGain = function (gain) {
        var i, tags = [].concat(this.effectsTag);
        if (this.effectsAvailable && this.effectsAllowed) {
            for (i = 0; i < tags.length; i += 1) {
                tbAudio.setGain(tags[i], gain);
            }
        }
    };
    // Set gain for all music
    TGFAudio.prototype.setMusicGain = function (gain) {
        var i, tags = [].concat(this.musicTag);
        if (this.musicAvailable && this.musicAllowed) {
            for (i = 0; i < tags.length; i += 1) {
                tbAudio.setGain(tags[i], gain);
            }
        }
    };
    // ***************************************
    // Playing sound effects and music
    //		- These simply replace the same methods in tbAudio
    // ***************************************
    TGFAudio.prototype.play = function (item, delay, fadeDuration, fadeEase, looping) {
        if (!this.canPlay(item)) {
            return undefined;
        }
        var result = tbAudio.play(item, delay, fadeDuration, fadeEase, looping);
        if (result === -1) {
            return undefined;
        }
        return result;
    };
    TGFAudio.prototype.stop = function (tag, fadeDuration, fadeEase) {
        if (tag !== undefined) {
            tbAudio.stop(tag, fadeDuration, fadeEase);
        }
    };
    TGFAudio.prototype.isPlaying = function (id) {
        return id !== undefined ? tbAudio.isPlaying(id) : false;
    };
    TGFAudio.prototype.isActive = function (id) {
        return id !== undefined ? tbAudio.isActive(id) : false;
    };
    TGFAudio.prototype.pause = function (tag, duration, fadeDuration, fadeEase) {
        if (tag !== undefined) {
            tbAudio.pause(tag, duration, fadeDuration, fadeEase);
        }
    };
    TGFAudio.prototype.resume = function (tag, fadeDuration, fadeEase) {
        if (tag !== undefined) {
            tbAudio.resume(tag, fadeDuration, fadeEase);
        }
    };
    TGFAudio.prototype.isPaused = function (id) {
        return id !== undefined ? tbAudio.isPaused(id) : false;
    };
    TGFAudio.prototype.fade = function (tag, targetValue, duration, easeFunction, callback) {
        if (tag !== undefined) {
            tbAudio.fade(tag, targetValue, duration, easeFunction, callback);
        }
    };
    TGFAudio.prototype.getFading = function (id) {
        return id !== undefined ? tbAudio.getFading(id) : 0;
    };
    TGFAudio.prototype.setLooping = function (tag, looping) {
        if (tag !== undefined) {
            tbAudio.setLooping(tag, looping);
        }
    };
    TGFAudio.prototype.isLooping = function (id) {
        return id !== undefined ? tbAudio.isLooping(id) : false;
    };
    return TGFAudio;
}());
// ***************************************
// Create the object
// ***************************************
export var tgfAudio = new TGFAudio();
